/**
 * WebSocket client for real-time messaging (typing, read receipts, new-message notifications).
 * Connects with ?token=idToken. Set VITE_WS_URL to the WebSocket API URL (wss://...).
 */

const WS_URL = (import.meta?.env?.VITE_WS_URL || '').trim();

let ws = null;
const messageHandlers = new Set();
let reconnectAttempts = 0;
let reconnectTimer = null;
let intentionalClose = false;
const MAX_RECONNECT_ATTEMPTS = 10;
const INITIAL_RECONNECT_MS = 1000;
const MAX_RECONNECT_MS = 30000;

function getToken() {
  try {
    const raw = localStorage.getItem('user');
    const u = raw ? JSON.parse(raw) : null;
    return u?.tokens?.idToken || u?.tokens?.IdToken || u?.idToken || u?.IdToken || '';
  } catch {
    return '';
  }
}

function getUrl() {
  if (!WS_URL) return null;
  const token = getToken();
  if (!token) return null;
  const sep = WS_URL.includes('?') ? '&' : '?';
  return `${WS_URL}${sep}token=${encodeURIComponent(token)}`;
}

function reconnectDelay() {
  const ms = Math.min(
    INITIAL_RECONNECT_MS * Math.pow(2, reconnectAttempts),
    MAX_RECONNECT_MS
  );
  reconnectAttempts += 1;
  return ms;
}

function emit(data) {
  messageHandlers.forEach((handler) => {
    try {
      handler(data);
    } catch (e) {
      console.warn('[WS] Handler error:', e);
    }
  });
}

export const wsClient = {
  connect() {
    const url = getUrl();
    if (!url) {
      if (import.meta?.env?.DEV) console.warn('[WS] No VITE_WS_URL or token, skip connect');
      return false;
    }
    if (ws?.readyState === WebSocket.OPEN) return true;
    intentionalClose = false;
    try {
      ws = new WebSocket(url);
    } catch (e) {
      console.error('[WS] Connect error:', e);
      return false;
    }
    ws.onopen = () => {
      reconnectAttempts = 0;
      if (import.meta?.env?.DEV) console.log('[WS] Connected');
    };
    ws.onmessage = (ev) => {
      try {
        const data = JSON.parse(ev.data);
        emit(data);
      } catch {
        emit({ raw: ev.data });
      }
    };
    ws.onclose = () => {
      if (import.meta?.env?.DEV) console.log('[WS] Closed');
      ws = null;
      if (!intentionalClose) wsClient.scheduleReconnect();
    };
    ws.onerror = (e) => {
      console.warn('[WS] Error:', e);
    };
    return true;
  },

  disconnect() {
    intentionalClose = true;
    if (reconnectTimer) {
      clearTimeout(reconnectTimer);
      reconnectTimer = null;
    }
    reconnectAttempts = MAX_RECONNECT_ATTEMPTS;
    if (ws) {
      ws.close();
      ws = null;
    }
  },

  reconnect() {
    intentionalClose = true;
    if (reconnectTimer) {
      clearTimeout(reconnectTimer);
      reconnectTimer = null;
    }
    reconnectAttempts = 0;
    intentionalClose = false;
    return this.connect();
  },

  scheduleReconnect() {
    if (intentionalClose || reconnectAttempts >= MAX_RECONNECT_ATTEMPTS) return;
    const ms = reconnectDelay();
    if (import.meta?.env?.DEV) console.log('[WS] Reconnect in', ms, 'ms');
    reconnectTimer = setTimeout(() => {
      reconnectTimer = null;
      wsClient.connect();
    }, ms);
  },

  onMessage(handler) {
    if (typeof handler !== 'function') return () => {};
    messageHandlers.add(handler);
    return () => {
      messageHandlers.delete(handler);
    };
  },

  send(payload) {
    if (!ws || ws.readyState !== WebSocket.OPEN) return false;
    try {
      ws.send(JSON.stringify(payload));
      return true;
    } catch (e) {
      console.warn('[WS] Send error:', e);
      return false;
    }
  },

  sendTyping(conversationId) {
    return this.send({ action: 'typing', conversation_id: conversationId });
  },

  sendReadReceipt(conversationId, messageId) {
    return this.send({ action: 'read_receipt', conversation_id: conversationId, message_id: messageId });
  },

  sendConversationReadReceipt(conversationId) {
    return this.send({ action: 'read_receipt', conversation_id: conversationId, conversation_read: true });
  },

  sendNewMessageNotification(conversationId, message) {
    return this.send({
      action: 'new_message',
      conversation_id: conversationId,
      message: message != null ? message : {},
    });
  },

  isConnected() {
    return !!ws && ws.readyState === WebSocket.OPEN;
  },

  isConfigured() {
    return !!WS_URL;
  },
};

export default wsClient;
